import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

export const fetchFormDetails = createAsyncThunk(
  'questionSlice/fetchFormValues',
  async ({ code, id }, thunkAPI) => {
    const state = await thunkAPI.getState();
    const formValues = state.questionSlice.formValues.find((value) => value.tx_id === code);
    const errors = state.questionSlice.errors.find((value) => value.tx_id === code);
    const touchedFields = state.questionSlice.touchedFields.find((value) => value.tx_id === code);
    return {
      value: formValues?.values?.[id] || null,
      error: errors?.values?.[id] || null,
      isTouched: touchedFields?.fields?.[id] || false,
    };
  }
);

const initialState = {
  questionDetails: [],
  orderDetails: [],
  companyDetails: [],
  formValues: [],
  touchedFields: [],
  documents: {},
  stepData: {},
  languages: {},
  errors: [],
  creditCardInfo: [],
  paddingDetails: [],
  additionalParticipants: [],
  convertedTexts: [],
  dlDetails: {},
  qrDetails: {},
  idvDetails: {},
  idvQrDetails: {},
  idOnlyUploadStarted: {},
  bankDetails: {},
  incompleteSections: {},
  incompleteSectionsSidebar: {},
  fieldsToDisable: [],
  maxViewStep: {},
  visitedSectionIndex: {},
  introModalChecked: {},
  sectionComments: [],
  sectionPopupAcknowledged: [],
  last_activity_time: {},
  bankOwnerManualSelections: {},
};

const questionSlice = createSlice({
  name: 'questionSlice',
  initialState,
  reducers: {
    addDlDetails: (state, action) => {
      if (state?.dlDetails) {
        state.dlDetails[action.payload.tx_id] = action.payload.data;
      } else {
        state.dlDetails = { [action.payload.tx_id]: action.payload.data };
      }
    },
    removeDlDetails: (state, action) => {
      if (state?.dlDetails?.[action.payload.tx_id]) {
        delete state.dlDetails[action.payload.tx_id];
      }
    },
    addBankDetails: (state, action) => {
      if (state?.bankDetails?.[action.payload.tx_id]) {
        state.bankDetails[action.payload.tx_id][action.payload.type] = action.payload.data;
      } else {
        if (state?.bankDetails) {
          state.bankDetails[action.payload.tx_id] = { [action.payload.type]: action.payload.data };
        } else {
          state.bankDetails = {
            [action.payload.tx_id]: { [action.payload.type]: action.payload.data },
          };
        }
      }
    },
    removeBankDetails: (state, action) => {
      if (state?.bankDetails?.[action.payload.tx_id]) {
        delete state.bankDetails[action.payload.tx_id];
      }
    },
    addIncompleteSectionDetails: (state, action) => {
      if (state?.incompleteSections) {
        state.incompleteSections[action.payload.tx_id] = action.payload.data;
      } else {
        state.incompleteSections = { [action.payload.tx_id]: action.payload.data };
      }
    },
    removeIncompleteSectionDetails: (state, action) => {
      if (state?.incompleteSections?.[action.payload.tx_id]) {
        delete state.incompleteSections[action.payload.tx_id];
      }
    },
    addIncompleteSectionSidebarDetails: (state, action) => {
      if (state?.incompleteSectionsSidebar) {
        state.incompleteSectionsSidebar[action.payload.tx_id] = action.payload.data;
      } else {
        state.incompleteSectionsSidebar = { [action.payload.tx_id]: action.payload.data };
      }
    },
    removeIncompleteSectionSidebarDetails: (state, action) => {
      if (state?.incompleteSectionsSidebar?.[action.payload.tx_id]) {
        delete state.incompleteSectionsSidebar[action.payload.tx_id];
      }
    },
    addQRDetails: (state, action) => {
      if (state?.qrDetails) {
        state.qrDetails[action.payload.tx_id] = action.payload.data;
      } else {
        state.qrDetails = { [action.payload.tx_id]: action.payload.data };
      }
    },
    removeQRDetails: (state, action) => {
      if (state?.qrDetails?.[action.payload.tx_id]) {
        delete state.qrDetails[action.payload.tx_id];
      }
    },
    addIdvDetails: (state, action) => {
      const { tx_id, participantKey, data } = action.payload;
      if (!state.idvDetails) {
        state.idvDetails = {};
      }
      if (!state.idvDetails[tx_id]) {
        state.idvDetails[tx_id] = {};
      }
      state.idvDetails[tx_id][participantKey] = data;
    },
    removeIdvDetails: (state, action) => {
      const { tx_id, participantKey } = action.payload;
      if (participantKey && state?.idvDetails?.[tx_id]?.[participantKey]) {
        delete state.idvDetails[tx_id][participantKey];
      } else if (!participantKey && state?.idvDetails?.[tx_id]) {
        delete state.idvDetails[tx_id];
      }
    },
    addIdvQrDetails: (state, action) => {
      const { tx_id, participantKey, data } = action.payload;
      if (!state.idvQrDetails) {
        state.idvQrDetails = {};
      }
      if (!state.idvQrDetails[tx_id]) {
        state.idvQrDetails[tx_id] = {};
      }
      state.idvQrDetails[tx_id][participantKey] = data;
    },
    removeIdvQrDetails: (state, action) => {
      const { tx_id, participantKey } = action.payload;
      if (participantKey && state?.idvQrDetails?.[tx_id]?.[participantKey]) {
        delete state.idvQrDetails[tx_id][participantKey];
      } else if (!participantKey && state?.idvQrDetails?.[tx_id]) {
        delete state.idvQrDetails[tx_id];
      }
    },
    setIdOnlyUploadStarted: (state, action) => {
      const { tx_id } = action.payload;
      if (!state.idOnlyUploadStarted) {
        state.idOnlyUploadStarted = {};
      }
      state.idOnlyUploadStarted[tx_id] = true;
    },
    addQuestionDetails: (state, action) => {
      let data = {
        tx_id: action.payload.tx_id,
        questions: action.payload.questions,
        sources: action.payload.sources,
        participants: action.payload.participants || [],
        is_pdf_generated: action.payload.is_pdf_generated,
        template_id: action.payload.template_id,
        order_part_participant_id: action.payload.order_part_participant_id,
        documents: action.payload.documents,
        additional_participants: action?.payload?.additional_participants,
        participant: action?.payload?.participant,
      };
      const isAvailable = state?.questionDetails?.find(
        (question) => question.tx_id === action.payload.tx_id
      );
      if (isAvailable) {
        state.questionDetails = state.questionDetails.filter(
          (question) => question.tx_id !== action.payload.tx_id
        );
      }
      state?.questionDetails?.push(data);
    },
    addOrderDetails: (state, action) => {
      let data = {
        tx_id: action.payload.tx_id,
        order: action.payload.order,
      };
      const isAvailable = state?.orderDetails?.find(
        (order) => order.tx_id === action.payload.tx_id
      );
      if (isAvailable) {
        state.orderDetails = state.orderDetails.filter(
          (order) => order.tx_id !== action.payload.tx_id
        );
      }
      state?.orderDetails?.push(data);
    },
    addCompanyDetails: (state, action) => {
      let data = {
        tx_id: action.payload.tx_id,
        company: action.payload.company,
      };
      const isAvailable = state?.companyDetails?.find(
        (company) => company.tx_id === action.payload.tx_id
      );
      if (isAvailable) {
        state.companyDetails = state.companyDetails.filter(
          (company) => company.tx_id !== action.payload.tx_id
        );
      }

      state?.companyDetails.push(data);
    },
    addFormValue: (state, action) => {
      const index = state?.formValues?.findIndex((form) => form.tx_id === action.payload.tx_id);
      if (index !== -1) {
        state.formValues[index].values[action.payload.id] = action.payload.value;
      } else {
        state.formValues.push({
          tx_id: action.payload.tx_id,
          values: { [action.payload.id]: action.payload.value },
        });
      }
    },
    clearFormValue: (state, action) => {
      const index = state?.formValues?.findIndex((form) => form.tx_id === action.payload.tx_id);
      action.payload.ids.forEach((e) => delete state.formValues?.[index]?.values[e]);
    },
    clearFormValues: (state, action) => {
      state.formValues = state.formValues.filter((form) => form.tx_id !== action.payload.tx_id);
    },
    addTouchedFields: (state, action) => {
      const index = state?.touchedFields?.findIndex((form) => form.tx_id === action.payload.tx_id);
      if (index !== -1) {
        state.touchedFields[index].fields[action.payload.id] = true;
      } else {
        state.touchedFields.push({
          tx_id: action.payload.tx_id,
          fields: { [action.payload.id]: true },
        });
      }
    },
    clearTouchedField: (state, action) => {
      const index = state?.touchedFields?.findIndex((form) => form.tx_id === action.payload.tx_id);
      action.payload.ids.forEach((e) => delete state.touchedFields?.[index]?.fields[e]);
    },
    clearTouchedFields: (state, action) => {
      state.touchedFields = state.touchedFields.filter(
        (form) => form.tx_id !== action.payload.tx_id
      );
    },
    addErrorDetails: (state, action) => {
      const index = state?.errors?.findIndex((error) => error.tx_id === action.payload.tx_id);
      if (index !== -1) {
        state.errors[index].values[action.payload.id] = {
          error: action.payload.error,
          error_message: action.payload.error_message,
          error_type: action.payload.error_type,
        };
      } else {
        state.errors.push({
          tx_id: action.payload.tx_id,
          values: {
            [action.payload.id]: {
              error: action.payload.error,
              error_message: action.payload.error_message,
              error_type: action.payload.error_type,
            },
          },
        });
      }
    },
    clearErrorDetail: (state, action) => {
      const index = state?.errors?.findIndex((error) => error.tx_id === action.payload.tx_id);
      delete state?.errors?.[index]?.values?.[action.payload.id];
    },
    clearErrorDetails: (state, action) => {
      state.errors = state.errors.filter((error) => error.tx_id !== action.payload.tx_id);
    },
    setDocuments: (state, action) => {
      state.documents[action.payload.tx_id] = action.payload.documents;
    },
    clearQuestionStore: (state, action) => {
      state.formValues = state.formValues.filter((form) => form.tx_id !== action.payload.tx_id);
      state.errors = state.errors.filter((error) => error.tx_id !== action.payload.tx_id);
      state.companyDetails = state.companyDetails.filter(
        (company) => company.tx_id !== action.payload.tx_id
      );
      state.orderDetails = state.orderDetails.filter(
        (order) => order.tx_id !== action.payload.tx_id
      );
      state.questionDetails = state.questionDetails.filter(
        (question) => question.tx_id !== action.payload.tx_id
      );
      delete state.documents[action.payload.tx_id];
    },
    addCurrentStep: (state, action) => {
      state.stepData[action.payload.tx_id] = action.payload.step;
    },
    removeCurrentStep: (state, action) => {
      state.stepData[action.payload.tx_id] = 0;
    },
    addMaxViewStep: (state, action) => {
      if (state?.maxViewStep) {
        state.maxViewStep[action.payload.tx_id] = action.payload.step;
      } else {
        state.maxViewStep = { [action.payload.tx_id]: action.payload.step };
      }
    },
    removeMaxViewStep: (state, action) => {
      state.maxViewStep[action.payload.tx_id] = 0;
    },
    addVisitedSectionIndex: (state, action) => {
      if (state?.visitedSectionIndex) {
        state.visitedSectionIndex[action.payload.tx_id] = action.payload.visitedSectionIndex;
      } else {
        state.visitedSectionIndex = { [action.payload.tx_id]: action.payload.visitedSectionIndex };
      }
    },
    removeVisitedSectionIndex: (state, action) => {
      delete state.visitedSectionIndex[action.payload.tx_id];
    },
    addIntroModalChecked: (state, action) => {
      if (state?.introModalChecked) {
        state.introModalChecked[action.payload.tx_id] = action.payload.introModalChecked;
      } else {
        state.introModalChecked = { [action.payload.tx_id]: action.payload.introModalChecked };
      }
    },
    removeIntroModalChecked: (state, action) => {
      delete state.introModalChecked[action.payload.tx_id];
    },
    setBankOwnerManualSelection: (state, action) => {
      const { tx_id, element_id } = action.payload;
      if (!state?.bankOwnerManualSelections) state.bankOwnerManualSelections = {};
      state.bankOwnerManualSelections[tx_id] = {
        ...(state.bankOwnerManualSelections[tx_id] || {}),
        [element_id]: true,
      };
    },
    addCreditCardInfo: (state, action) => {
      let data = {
        show: action.payload.show,
        info: action.payload.info,
        tx_id: action.payload.tx_id,
      };
      const isAvailable = state?.creditCardInfo?.find(
        (question) => question.tx_id === action.payload.tx_id
      );
      if (isAvailable) {
        state.creditCardInfo = state.creditCardInfo.filter(
          (question) => question.tx_id !== action.payload.tx_id
        );
      }
      if (state?.creditCardInfo?.length) {
        state?.creditCardInfo?.push(data);
      } else {
        state.creditCardInfo = [data];
      }
    },
    addPaddingDetails: (state, action) => {
      let data = {
        isCollapsed: action.payload.isCollapsed,
        padding: action.payload.padding,
        tx_id: action.payload.tx_id,
      };
      const isAvailable = state?.paddingDetails?.find(
        (question) => question.tx_id === action.payload.tx_id
      );
      if (isAvailable) {
        state.paddingDetails = state.paddingDetails.filter(
          (question) => question.tx_id !== action.payload.tx_id
        );
      }
      if (state?.paddingDetails?.length) {
        state?.paddingDetails?.push(data);
      } else {
        state.paddingDetails = [data];
      }
    },
    additionalParticipantToggle: (state, action) => {
      let data = {
        value: action.payload.value,
        tx_id: action.payload.tx_id,
      };
      const isAvailable = state?.additionalParticipants?.find(
        (question) => question.tx_id === action.payload.tx_id
      );
      if (isAvailable) {
        state.additionalParticipants = state.additionalParticipants.filter(
          (question) => question.tx_id !== action.payload.tx_id
        );
      }
      if (state?.additionalParticipants?.length) {
        state?.additionalParticipants?.push(data);
      } else {
        state.additionalParticipants = [data];
      }
    },
    addLanguage: (state, action) => {
      if (!Object.keys(state?.languages || {})?.length) {
        state.languages = { [action.payload.tx_id]: action.payload.language };
      } else {
        state.languages[action.payload.tx_id] = action.payload.language;
      }
    },
    addConvertedTexts: (state, action) => {
      let data = {
        tx_id: action.payload.tx_id,
        [action.payload.language]: action.payload.translations,
      };
      const isAvailable = state?.convertedTexts?.find(
        (question) => question.tx_id === action.payload.tx_id
      );
      if (isAvailable) {
        state.convertedTexts = state.convertedTexts.filter(
          (question) => question.tx_id !== action.payload.tx_id
        );
      }
      if (state?.convertedTexts?.length) {
        state?.convertedTexts?.push(data);
      } else {
        state.convertedTexts = [data];
      }
    },
    removeConvertedTexts: (state, action) => {
      state.convertedTexts = state.convertedTexts.filter(
        (question) => question.tx_id !== action.payload.tx_id
      );
    },
    addFieldsToDisable: (state, action) => {
      if (state?.fieldsToDisable?.[action.payload.tx_id]) {
        state.fieldsToDisable[action.payload.tx_id] = action.payload.fields;
      } else {
        state.fieldsToDisable = { [action.payload.tx_id]: action.payload.fields };
      }
    },
    removeFieldsToDisable: (state, action) => {
      if (state?.fieldsToDisable?.[action.payload.tx_id]) {
        delete state.fieldsToDisable[action.payload.tx_id];
      }
    },
    addSectionComment: (state, action) => {
      const index = state?.sectionComments?.findIndex(
        (comment) => comment.tx_id === action.payload.tx_id
      );
      if (index !== -1) {
        state.sectionComments[index].comments[action.payload.section_id] = action.payload.comment;
      } else {
        state.sectionComments.push({
          tx_id: action.payload.tx_id,
          comments: { [action.payload.section_id]: action.payload.comment },
        });
      }
    },
    clearSectionComment: (state, action) => {
      const index = state?.sectionComments?.findIndex(
        (comment) => comment.tx_id === action.payload.tx_id
      );
      delete state.sectionComments?.[index]?.comments?.[action.payload.section_id];
    },
    clearSectionComments: (state, action) => {
      delete state.sectionComments?.[action.payload.tx_id];
    },
    addSectionPopupAcknowledged: (state, action) => {
      const index = state?.sectionPopupAcknowledged?.findIndex(
        (section) => section.tx_id === action.payload.tx_id
      );
      if (index !== -1) {
        state.sectionPopupAcknowledged[index].sections[action.payload.section_id] = true;
      } else {
        state?.sectionPopupAcknowledged?.push({
          tx_id: action.payload.tx_id,
          sections: { [action.payload.section_id]: true },
        });
      }
    },
    updateLastActivityTime: (state, action) => {
      if (state?.last_activity_time) {
        state.last_activity_time[action.payload.code] = action.payload.timestamp;
      } else {
        state.last_activity_time = { [action.payload.code]: action.payload.timestamp };
      }
    },
    clearQuestionSlice: (state, action) => {
      let tempState = { ...initialState, companyDetails: state?.companyDetails };
      return tempState;
    },
  },
});

export const {
  addQRDetails,
  clearQuestionSlice,
  removeQRDetails,
  addDlDetails,
  removeDlDetails,
  addIdvDetails,
  removeIdvDetails,
  addIdvQrDetails,
  removeIdvQrDetails,
  setIdOnlyUploadStarted,
  addQuestionDetails,
  addOrderDetails,
  addCompanyDetails,
  addFormValue,
  clearFormValue,
  clearFormValues,
  addTouchedFields,
  clearTouchedField,
  clearTouchedFields,
  addErrorDetails,
  clearErrorDetail,
  clearErrorDetails,
  setDocuments,
  addCurrentStep,
  clearQuestionStore,
  removeCurrentStep,
  addCreditCardInfo,
  additionalParticipantToggle,
  addPaddingDetails,
  addLanguage,
  addConvertedTexts,
  removeConvertedTexts,
  addBankDetails,
  removeBankDetails,
  addIncompleteSectionDetails,
  removeIncompleteSectionDetails,
  addFieldsToDisable,
  removeFieldsToDisable,
  addIncompleteSectionSidebarDetails,
  removeIncompleteSectionSidebarDetails,
  addMaxViewStep,
  removeMaxViewStep,
  addVisitedSectionIndex,
  removeVisitedSectionIndex,
  addIntroModalChecked,
  removeIntroModalChecked,
  setBankOwnerManualSelection,
  addSectionComment,
  clearSectionComment,
  clearSectionComments,
  addSectionPopupAcknowledged,
  updateLastActivityTime,
} = questionSlice.actions;

export default questionSlice.reducer;
